import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { format } from "date-fns";
import { useHistory } from "react-router-dom";
import Modal from "elements/Modal";
import { useVideoPlayer } from "components/VideoPlayer/useVideoPlayer";
import VideoPlayer from "components/VideoPlayer";
import { ANLoadingScreen } from "@answersnow/pkg-theme";
import { capitalizeBillingTypeFirstLetter } from "../../../../../utils/capitalizeBillingTypeFirstLetter";
import { VideoCall } from "./types";
import { tableHeaders } from "./TableHeaders";
import { EmptyState } from "./EmptyState";
import { CallsTableWrapper } from "./CallsTableWrapper";
import { CallTableRow } from "./CallTableRow";
import { useClientVideoCalls } from "hooks/api/client/useClientVideoCalls";
import { CalendarDots } from "@phosphor-icons/react";
import { USER_ID_PARAM } from "@constants/calendarFilter";

const toggleButtonSx = {
  textTransform: "none",
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: "22px",
  letterSpacing: "0.46px",
  px: 2,
  py: "6px",
  color: "text.primary",
  "&.Mui-selected": {
    bgcolor: "action.selected",
    color: "text.primary",
    "&:hover": { bgcolor: "action.selected" },
  },
  "&:not(.Mui-selected)": {
    color: "text.primary",
    bgcolor: "transparent",
    "&:hover": { bgcolor: "action.hover" },
  },
} as const;

const SKELETON_ROW_COUNT = 5;
const HEADER_LABELS = ["Start Time", "End Time", "Duration", "Service", "Details", "Actions"];

interface ClientCallsTableWithHooksProps {
  clientId: string;
  navigate?: any;
  userPermissions?: any;
  customerDetails?: any;
  editDisabled?: boolean;
  onClickCancelSession?: (videoCall: VideoCall) => void;
  currentUser?: any;
  /** When false, renders a skeleton placeholder matching the table layout. */
  customerLoaded?: boolean;
}

/**
 * Displays the client's appointment history and upcoming calls in a filterable
 * paginated table. Supports "Upcoming Calls" and "Past Calls" tabs.
 */
export const ClientCallsTableWithHooks: React.FC<ClientCallsTableWithHooksProps> = ({
  clientId,
  navigate,
  userPermissions,
  customerDetails,
  editDisabled,
  onClickCancelSession,
  customerLoaded = true,
}) => {
  const history = useHistory();
  const currentUserId = customerDetails?.details?.userid;
  const clientName = [customerDetails?.details?.name, customerDetails?.details?.child_last_name]
    .filter(Boolean)
    .join(" ");
  const canModifyEvent = userPermissions?.select_scheduled_clinician;
  const [currentTab, setCurrentTab] = useState<"upcoming" | "past">("upcoming");
  const [upcomingPage, setUpcomingPage] = useState(0);
  const [upcomingRowsPerPage, setUpcomingRowsPerPage] = useState(10);
  const [pastPage, setPastPage] = useState(0);
  const [pastRowsPerPage, setPastRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<{ [key: number]: HTMLElement | null }>({});
  const [selectedVideoCall, setSelectedVideoCall] = useState<VideoCall | null>(null);
  const [noSessionNotesDialogOpen, setNoSessionNotesDialogOpen] = useState(false);
  const modalControl = useVideoPlayer();

  const {
    videoCalls: upcomingCalls,
    videoCallsLoading: upcomingLoading,
    pagination: upcomingPagination,
    refetchVideoCalls: refetchUpcoming,
  } = useClientVideoCalls(clientId, "upcoming", {
    page: upcomingPage,
    size: upcomingRowsPerPage,
  });

  const {
    videoCalls: pastCalls,
    videoCallsLoading: pastLoading,
    pagination: pastPagination,
    refetchVideoCalls: refetchPast,
  } = useClientVideoCalls(clientId, "past", {
    page: pastPage,
    size: pastRowsPerPage,
  });

  const handleTabChange = (event: React.MouseEvent<HTMLElement>, newValue: "upcoming" | "past") => {
    if (newValue !== null) setCurrentTab(newValue);
  };

  const handleUpcomingPageChange = (event: unknown, newPage: number) => {
    setUpcomingPage(newPage);
  };

  const handleUpcomingRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUpcomingRowsPerPage(parseInt(event.target.value, 10));
    setUpcomingPage(0);
  };

  const handlePastPageChange = (event: unknown, newPage: number) => {
    setPastPage(newPage);
  };

  const handlePastRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPastRowsPerPage(parseInt(event.target.value, 10));
    setPastPage(0);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, callId: number) => {
    setAnchorEl({ ...anchorEl, [callId]: event.currentTarget });
  };

  const handleCloseMenu = (callId: number) => {
    setAnchorEl({ ...anchorEl, [callId]: null });
  };

  const downloadAll = useCallback(async (videoCallId: number) => {
    const baseUrl = process.env.BASE_URL;
    const downloadUrl = `${baseUrl}/download/all-session-notes/${videoCallId}`;
    window.location.assign(downloadUrl);
  }, []);

  const handleWatchVideo = (videoCall: VideoCall) => {
    if (videoCall.end_date && videoCall.video_key) {
      setSelectedVideoCall(videoCall);
      modalControl.openModal();
    }
    handleCloseMenu(videoCall.id);
  };

  const handleViewDetails = (videoCall: VideoCall) => {
    if (navigate) {
      const url =
        currentTab === "upcoming"
          ? `/clinician-schedule?${USER_ID_PARAM}=${videoCall.assigned_team_member_id}`
          : `/video-calls/${videoCall.id}`;
      navigate(url);
    }
  };

  const handleReschedule = (videoCall: VideoCall) => {
    history.push(`/clinician-schedule/create-event?id=${videoCall.id}`);
    handleCloseMenu(videoCall.id);
  };

  const handleCancel = (videoCall: VideoCall) => {
    onClickCancelSession?.(videoCall);
    handleCloseMenu(videoCall.id);

    setTimeout(() => {
      if (currentTab === "upcoming") {
        refetchUpcoming();
      } else {
        refetchPast();
      }
    }, 3000);
  };

  const renderCallsList = (
    calls: VideoCall[] | null,
    isLoading: boolean,
    pagination: any,
    onPageChange: (event: unknown, newPage: number) => void,
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  ) => {
    if (isLoading || !calls) {
      return <ANLoadingScreen />;
    }

    if (calls.length === 0) {
      return <EmptyState />;
    }

    return (
      <CallsTableWrapper
        headerCells={tableHeaders}
        count={pagination?.total || 0}
        rowsPerPage={pagination?.size || 10}
        page={pagination?.page || 0}
        handleChangePage={onPageChange}
        handleChangeRowsPerPage={onRowsPerPageChange}
      >
        {calls.map((videoCall) => {
          const currentUserAssignedToCall = currentUserId === videoCall?.clinician_user_id;
          return (
            <CallTableRow
              key={videoCall.id}
              videoCall={videoCall}
              anchorEl={anchorEl[videoCall.id]}
              onOpenMenu={(e) => handleOpenMenu(e, videoCall.id)}
              onCloseMenu={() => handleCloseMenu(videoCall.id)}
              onDownloadNotes={() => downloadAll(videoCall.id)}
              onWatchVideo={() => handleWatchVideo(videoCall)}
              onViewDetails={() => handleViewDetails(videoCall)}
              onOpenDataSheet={
                process.env.CENTRAL_REACH_URL
                  ? () => {
                      const base = process.env.CENTRAL_REACH_URL!.replace(/\/$/, "");
                      // start_date (when the call actually started) is the
                      // primary timestamp elsewhere in this table (see
                      // CallTableRow's "Start Time" column); scheduled_date
                      // is only a fallback for calls that never started.
                      const sessionDate = format(
                        new Date(videoCall.start_date ?? videoCall.scheduled_date),
                        "yyyy-MM-dd"
                      );
                      window.open(
                        `${base}/data-sheets/${encodeURIComponent(clientId)}/appointment/${encodeURIComponent(String(videoCall.id))}?date=${encodeURIComponent(sessionDate)}`,
                        "_blank",
                        "noopener,noreferrer"
                      );
                      handleCloseMenu(videoCall.id);
                    }
                  : undefined
              }
              onReschedule={() => handleReschedule(videoCall)}
              onCancel={() => handleCancel(videoCall)}
              userPermissions={userPermissions}
              currentTab={currentTab}
              canModifyEvent={canModifyEvent}
              currentUserAssignedToCall={currentUserAssignedToCall}
            />
          );
        })}
      </CallsTableWrapper>
    );
  };

  if (!customerLoaded) {
    return (
      <Grid
        container
        component="section"
        aria-label="Loading appointments"
        role="status"
        aria-live="polite"
        data-testid="appointments-loading"
        sx={{
          py: "16px",
          px: "8px",
          gap: "16px",
          borderRadius: "16px",
          bgcolor: "grey.50",
          my: 7,
          maxWidth: "1200px",
          mx: "auto",
        }}
      >
        <Grid
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "8px",
            py: 0,
          }}
        >
          <Skeleton variant="text" width="20%" height={40} />
          <Skeleton variant="rounded" width="30%" height={36} sx={{ borderRadius: "12px" }} />
        </Grid>
        <Grid xs={12}>
          <Box
            sx={{
              borderRadius: "16px",
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Table sx={{ borderCollapse: "collapse" }}>
              <TableHead>
                <TableRow sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
                  {HEADER_LABELS.map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        p: 5,
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "24px",
                        letterSpacing: "0.17px",
                        color: "text.primary",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      borderBottom:
                        index < SKELETON_ROW_COUNT - 1 ? "1px solid" : "0px solid transparent",
                      borderColor: "divider",
                    }}
                  >
                    <TableCell sx={{ p: 5 }}>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                    <TableCell sx={{ p: 5 }}>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                    <TableCell sx={{ p: 5 }}>
                      <Skeleton variant="text" width="60%" />
                    </TableCell>
                    <TableCell sx={{ p: 5 }}>
                      <Skeleton variant="text" width="70%" />
                    </TableCell>
                    <TableCell sx={{ p: 5 }}>
                      <Skeleton variant="text" width="40%" />
                    </TableCell>
                    <TableCell sx={{ p: 5 }}>
                      <Skeleton variant="circular" width={24} height={24} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid
        container
        component="section"
        aria-labelledby="appointments-heading"
        sx={{
          py: "16px",
          px: "8px",
          gap: "16px",
          borderRadius: "16px",
          bgcolor: "grey.50",
          my: 7,
          maxWidth: "1200px",
          mx: "auto",
        }}
      >
        {/* Header */}
        <Grid
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            px: "8px",
            py: 0,
          }}
        >
          <Typography id="appointments-heading" component="h3" variant="h4">
            Appointments
          </Typography>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon={<CalendarDots />}
              onClick={() =>
                history.push(
                  `/clinician-schedule/create-event?name=${encodeURIComponent(clientName)}`
                )
              }
              disabled={editDisabled}
            >
              Create An Event
            </Button>

            <ToggleButtonGroup
              value={currentTab}
              exclusive
              size="small"
              onChange={handleTabChange}
              aria-label="Appointment filter"
              sx={{
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "12px",
                p: "4px",
                boxShadow: 1,
              }}
            >
              <ToggleButton value="upcoming" aria-label="Upcoming calls" sx={toggleButtonSx}>
                Upcoming Calls
              </ToggleButton>
              <ToggleButton value="past" aria-label="Past calls" sx={toggleButtonSx}>
                Past Calls
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Grid>

        {/* Table */}
        <Grid xs={12}>
          {currentTab === "upcoming" &&
            renderCallsList(
              upcomingCalls,
              upcomingLoading,
              upcomingPagination,
              handleUpcomingPageChange,
              handleUpcomingRowsPerPageChange
            )}
          {currentTab === "past" &&
            userPermissions?.view_client_call_schedule &&
            renderCallsList(
              pastCalls,
              pastLoading,
              pastPagination,
              handlePastPageChange,
              handlePastRowsPerPageChange
            )}
        </Grid>
      </Grid>

      <Modal
        open={noSessionNotesDialogOpen}
        title="No Session Notes Exist"
        description={
          <>
            This session does not have any session notes associated with it.
            <br /> Please contact the clinical department for more information.
          </>
        }
        primaryActionText="Close"
        primaryActionOnClick={() => setNoSessionNotesDialogOpen(false)}
      />

      {selectedVideoCall?.end_date && selectedVideoCall?.video_key && (
        <VideoPlayer
          videoKey={selectedVideoCall.video_key}
          videoPlayerTitle={
            selectedVideoCall.billing_type && selectedVideoCall.scheduled_date
              ? `${capitalizeBillingTypeFirstLetter(selectedVideoCall.billing_type)} Call - ${format(
                  new Date(selectedVideoCall.start_date || selectedVideoCall.scheduled_date),
                  "MMMM d, yyyy"
                )}`
              : "Video Call"
          }
          asModal={true}
          modalControl={modalControl}
        />
      )}
    </>
  );
};
